import React, { useState } from 'react';
import './SessionCard.css';

/**
 * Generic session/course card with status badges, meta info, and optional course/topic sections.
 *
 * Props:
 * - title: string (session title)
 * - status: { label: string, type?: 'active'|'pending'|'completed'|'upcoming'|'past' }
 * - secondaryStatus: optional status badge (e.g., registration status)
 * - meta: array<{ icon?: string, label: string, value: React.ReactNode }>
 * - course: {
 *     title?: string,
 *     description?: string,
 *     objectives?: React.ReactNode,
 *     expectations?: React.ReactNode
 *   }
 * - topics: array<{
 *     id?: string|number,
 *     name: string,
 *     hours?: React.ReactNode,
 *     description?: React.ReactNode,
 *     badges?: Array<{ label: string, type?: 'info'|'success'|'warning'|'danger' }>,
 *     dates?: Array<{ label: string, type?: 'default'|'success'|'warning'|'danger' }>,
 *     footer?: React.ReactNode
 *   }>
 * - actions: React.ReactNode (renders near bottom for CTA buttons)
 * - children: React.ReactNode (renders as additional sections after core content)
 */
const SessionCard = ({
  title,
  status,
  secondaryStatus,
  meta = [],
  course,
  topics = [],
  actions,
  children,
  className = '',
  defaultCollapsed = false
}) => {
  const [courseExpanded, setCourseExpanded] = useState(!defaultCollapsed);
  const [topicsExpanded, setTopicsExpanded] = useState(!defaultCollapsed);

  return (
    <div className={`session-card-shell status-${status?.type || 'default'} ${className}`}>
      <div className="session-card-header">
        <div>
          <h3>{title}</h3>
          {meta.length > 0 && (
            <ul className="session-card-meta" aria-label="Session quick facts">
              {meta.map((item, idx) => (
                <li key={idx} className="meta-item">
                  {item.icon && <span className="meta-icon" aria-hidden="true">{item.icon}</span>}
                  <span className="meta-label">{item.label}</span>
                  <span className="meta-value">{item.value}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="session-card-badges">
          {secondaryStatus && (
            <span className={`status-badge secondary ${secondaryStatus.type || 'info'}`}>
              {secondaryStatus.label}
            </span>
          )}
          {status && (
            <span className={`status-badge primary ${status.type || 'default'}`}>
              {status.label}
            </span>
          )}
        </div>
      </div>

      {course && (course.title || course.description || course.objectives || course.expectations) && (
        <div className="session-card-section course-section">
          <div 
            className="collapsible-header" 
            onClick={() => setCourseExpanded(!courseExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCourseExpanded(!courseExpanded);
              }
            }}
          >
            {course.title && <h4>{course.title}</h4>}
            <span className="collapse-icon">{courseExpanded ? '▼' : '▶'}</span>
          </div>
          {courseExpanded && (
            <div className="collapsible-content">
              {course.description && <p className="course-description">{course.description}</p>}
              {course.objectives && (
                <div className="course-callout objectives">
                  <strong>Course Objectives</strong>
                  <div>{course.objectives}</div>
                </div>
              )}
              {course.expectations && (
                <div className="course-callout expectations">
                  <strong>Expectations</strong>
                  <div>{course.expectations}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {topics.length > 0 && (
        <div className="session-card-section topics-section">
          <div 
            className="collapsible-header" 
            onClick={() => setTopicsExpanded(!topicsExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setTopicsExpanded(!topicsExpanded);
              }
            }}
          >
            <h4>Topics ({topics.length})</h4>
            <span className="collapse-icon">{topicsExpanded ? '▼' : '▶'}</span>
          </div>
          {topicsExpanded && (
            <div className="collapsible-content">
              <div className="topics-list">
                {topics.map((topic) => (
                  <div key={topic.id || topic.name} className="topic-card">
                    <div className="topic-header">
                      <span className="topic-name">{topic.name}</span>
                      <div className="topic-badges">
                        {topic.hours && (
                          <span className="topic-pill hours">{topic.hours}</span>
                        )}
                        {topic.badges?.map((badge, idx) => (
                          <span key={idx} className={`topic-pill ${badge.type || 'info'}`}>
                            {badge.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    {topic.description && (
                      <p className="topic-description">{topic.description}</p>
                    )}
                    {topic.dates && topic.dates.length > 0 && (
                      <div className="topic-dates">
                        {topic.dates.map((date, idx) => (
                          <span key={idx} className={`date-pill ${date.type || 'default'}`}>
                            {date.label}
                          </span>
                        ))}
                      </div>
                    )}
                    {topic.footer && (
                      <div className="topic-footer">
                        {topic.footer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {children && (
        <div className="session-card-section extra-content">
          {children}
        </div>
      )}

      {actions && (
        <div className="session-card-section actions-section">
          {actions}
        </div>
      )}
    </div>
  );
};

export default SessionCard;
